import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FileSpreadsheet, ArrowLeft, Phone, Mail, User,
  Download, Edit, Trash2, Filter, Search,
  ChevronLeft, ChevronRight, CheckCircle,
  XCircle, Clock, PhoneCall, Voicemail,
  PhoneMissed, Target, Users
} from 'lucide-react';

export default function ListDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [stats, setStats] = useState({
    total: 0,
    nouveaux: 0,
    qualifies: 0,
    convertis: 0,
    perdus: 0,
    appels: 0,
    dureeMoyenne: 0
  });

  const itemsPerPage = 20;

  useEffect(() => {
    loadListData();
  }, [id]);

  useEffect(() => {
    filterLeads();
  }, [searchTerm, statusFilter, leads]);

  async function loadListData() {
    try {
      setLoading(true);
      
      // Charger les infos de la liste
      const { data: listData, error: listError } = await supabase
        .from('campaigns')
        .select('*')
        .eq('id', id)
        .single();

      if (listError) throw listError;
      setList(listData);

      // Charger les leads de la liste
      const { data: leadsData, error: leadsError } = await supabase
        .from('leads')
        .select('*')
        .eq('campaign_id', id)
        .order('created_at', { ascending: false });

      if (leadsError) throw leadsError;

      // Charger les appels pour ces leads
      const leadIds = leadsData.map(l => l.id);
      const { data: callsData, error: callsError } = await supabase
        .from('calls')
        .select('*')
        .in('lead_id', leadIds);

      if (callsError) throw callsError;

      // Organiser les appels par lead
      const callsByLead = {};
      callsData.forEach(call => {
        if (!callsByLead[call.lead_id]) {
          callsByLead[call.lead_id] = [];
        }
        callsByLead[call.lead_id].push(call);
      });

      // Enrichir les leads avec leurs appels
      const leadsWithCalls = leadsData.map(lead => ({
        ...lead,
        calls: callsByLead[lead.id] || [],
        total_calls: (callsByLead[lead.id] || []).length,
        last_call: (callsByLead[lead.id] || [])[0] || null
      }));

      setLeads(leadsWithCalls);
      calculateStats(leadsWithCalls);
    } catch (error) {
      console.error('Erreur:', error);
      showNotification('Erreur chargement', 'error');
    } finally {
      setLoading(false);
    }
  }

  function calculateStats(leadsData) {
    const totalCalls = leadsData.reduce((acc, l) => acc + (l.total_calls || 0), 0);
    const totalDuration = leadsData.reduce((acc, l) => 
      acc + (l.calls?.reduce((sum, c) => sum + (c.duration || 0), 0) || 0), 0);

    setStats({
      total: leadsData.length,
      nouveaux: leadsData.filter(l => l.status === 'nouveau').length,
      qualifies: leadsData.filter(l => l.status === 'qualifié').length,
      convertis: leadsData.filter(l => l.status === 'converti').length,
      perdus: leadsData.filter(l => l.status === 'perdu').length,
      appels: totalCalls,
      dureeMoyenne: totalCalls ? Math.round(totalDuration / totalCalls) : 0
    });
  }

  function filterLeads() {
    let filtered = [...leads];

    if (searchTerm) {
      filtered = filtered.filter(lead =>
        lead.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.phone?.includes(searchTerm) ||
        lead.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(lead => lead.status === statusFilter);
    }

    setFilteredLeads(filtered);
  }

  function showNotification(message, type = 'success') {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
  }

  async function updateLeadStatus(leadId, newStatus) {
    try {
      const { error } = await supabase
        .from('leads')
        .update({ status: newStatus })
        .eq('id', leadId);

      if (error) throw error;

      setLeads(leads.map(lead => 
        lead.id === leadId ? { ...lead, status: newStatus } : lead
      ));

      showNotification(`Statut mis à jour: ${newStatus}`);
    } catch (error) {
      showNotification('Erreur mise à jour', 'error');
    }
  }

  async function addCall(leadId) {
    try {
      const duration = Math.floor(Math.random() * 180) + 60;
      const results = ['intéressé', 'boîte vocale', 'à rappeler', 'occupé', 'refusé'];
      const result = results[Math.floor(Math.random() * results.length)];

      // Ajouter l'appel
      const { error: callError } = await supabase
        .from('calls')
        .insert([{
          lead_id: leadId,
          duration: duration,
          result: result,
          created_at: new Date().toISOString()
        }]);

      if (callError) throw callError;

      // Mettre à jour le lead
      const { error: leadError } = await supabase
        .from('leads')
        .update({
          call_attempts: (leads.find(l => l.id === leadId).call_attempts || 0) + 1,
          last_call_duration: duration,
          last_call_result: result,
          last_call_time: new Date().toISOString()
        })
        .eq('id', leadId);

      if (leadError) throw leadError;

      showNotification('Appel enregistré');
      loadListData();
    } catch (error) {
      showNotification('Erreur appel', 'error');
    }
  }

  async function exportLeads() {
    try {
      const headers = ['name', 'phone', 'email', 'status', 'call_attempts', 'last_call_result', 'last_call_time', 'created_at'];
      const csv = [
        headers.join(','),
        ...filteredLeads.map(lead => 
          headers.map(h => `"${lead[h] || ''}"`).join(',')
        )
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${list?.name}_leads.csv`;
      a.click();
    } catch (error) {
      showNotification('Erreur export', 'error');
    }
  }

  const getStatusColor = (status) => {
    const colors = {
      'nouveau': 'bg-blue-500/20 text-blue-400',
      'qualifié': 'bg-green-500/20 text-green-400',
      'occupé': 'bg-yellow-500/20 text-yellow-400',
      'boîte vocale': 'bg-purple-500/20 text-purple-400',
      'à rappeler': 'bg-orange-500/20 text-orange-400',
      'converti': 'bg-emerald-500/20 text-emerald-400',
      'refusé': 'bg-red-500/20 text-red-400',
      'perdu': 'bg-gray-500/20 text-gray-400'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400';
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);
  const paginatedLeads = filteredLeads.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return <div className="text-center py-12">Chargement...</div>;
  }

  if (!list) {
    return <div className="text-center py-12">Liste non trouvée</div>;
  }

  return (
    <div className="space-y-6">
      {/* Notification */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg ${
          notification.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
        }`}>
          {notification.message}
        </div>
      )}

      {/* Navigation */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-400 hover:text-white"
      >
        <ArrowLeft size={18} />
        Retour
      </button>

      {/* En-tête */}
      <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl">
              <FileSpreadsheet className="text-white" size={32} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">{list.name}</h1>
              <p className="text-sm text-gray-500 mt-1">{list.description || 'Aucune description'}</p>
              <div className="flex gap-4 mt-2 text-sm">
                <span className="text-gray-400">Créée le {new Date(list.created_at).toLocaleDateString()}</span>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  list.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                }`}>
                  {list.status === 'active' ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={exportLeads}
            className="px-4 py-2 bg-cyan-600 rounded-lg text-white flex items-center gap-2"
          >
            <Download size={18} />
            Exporter CSV
          </button>
        </div>
      </div>

      {/* Stats de la liste */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <div className="bg-gray-900 p-3 rounded-lg border border-gray-800">
          <p className="text-xs text-gray-500">Total</p>
          <p className="text-xl font-bold text-white">{stats.total}</p>
        </div>
        <div className="bg-blue-500/10 p-3 rounded-lg border border-blue-500/20">
          <p className="text-xs text-blue-400">Nouveaux</p>
          <p className="text-xl font-bold text-blue-400">{stats.nouveaux}</p>
        </div>
        <div className="bg-green-500/10 p-3 rounded-lg border border-green-500/20">
          <p className="text-xs text-green-400">Qualifiés</p>
          <p className="text-xl font-bold text-green-400">{stats.qualifies}</p>
        </div>
        <div className="bg-emerald-500/10 p-3 rounded-lg border border-emerald-500/20">
          <p className="text-xs text-emerald-400">Convertis</p>
          <p className="text-xl font-bold text-emerald-400">{stats.convertis}</p>
        </div>
        <div className="bg-red-500/10 p-3 rounded-lg border border-red-500/20">
          <p className="text-xs text-red-400">Perdus</p>
          <p className="text-xl font-bold text-red-400">{stats.perdus}</p>
        </div>
        <div className="bg-purple-500/10 p-3 rounded-lg border border-purple-500/20">
          <p className="text-xs text-purple-400">Appels</p>
          <p className="text-xl font-bold text-purple-400">{stats.appels}</p>
        </div>
        <div className="bg-cyan-500/10 p-3 rounded-lg border border-cyan-500/20">
          <p className="text-xs text-cyan-400">Durée moy</p>
          <p className="text-xl font-bold text-cyan-400">{formatDuration(stats.dureeMoyenne)}</p>
        </div>
      </div>

      {/* Barre de progression */}
      <div className="bg-gray-900 p-4 rounded-xl border border-gray-800">
        <div className="flex justify-between mb-2">
          <span className="text-gray-400">Progression</span>
          <span className="text-white">
            {stats.total ? Math.round(((stats.total - stats.nouveaux) / stats.total) * 100) : 0}%
          </span>
        </div>
        <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-cyan-500 to-blue-500"
            style={{ width: `${stats.total ? ((stats.total - stats.nouveaux) / stats.total) * 100 : 0}%` }}
          />
        </div>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              statusFilter === 'all' ? 'bg-cyan-600 text-white' : 'bg-gray-900 text-gray-400 hover:text-white'
            }`}
          >
            Tous
          </button>
          <button
            onClick={() => setStatusFilter('nouveau')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              statusFilter === 'nouveau' ? 'bg-blue-600 text-white' : 'bg-gray-900 text-gray-400 hover:text-white'
            }`}
          >
            Nouveaux
          </button>
          <button
            onClick={() => setStatusFilter('qualifié')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              statusFilter === 'qualifié' ? 'bg-green-600 text-white' : 'bg-gray-900 text-gray-400 hover:text-white'
            }`}
          >
            Qualifiés
          </button>
          <button
            onClick={() => setStatusFilter('converti')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              statusFilter === 'converti' ? 'bg-emerald-600 text-white' : 'bg-gray-900 text-gray-400 hover:text-white'
            }`}
          >
            Convertis
          </button>
          <button
            onClick={() => setStatusFilter('perdu')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              statusFilter === 'perdu' ? 'bg-red-600 text-white' : 'bg-gray-900 text-gray-400 hover:text-white'
            }`}
          >
            Perdus
          </button>
        </div>

        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white w-64"
          />
        </div>
      </div>

      {/* Tableau des leads */}
      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800/50">
              <tr>
                <th className="p-3 text-left text-gray-300">Nom</th>
                <th className="p-3 text-left text-gray-300">Contact</th>
                <th className="p-3 text-left text-gray-300">Statut</th>
                <th className="p-3 text-left text-gray-300">Appels</th>
                <th className="p-3 text-left text-gray-300">Dernier appel</th>
                <th className="p-3 text-left text-gray-300">Durée</th>
                <th className="p-3 text-left text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {paginatedLeads.map(lead => (
                <tr key={lead.id} className="hover:bg-gray-800/30">
                  <td className="p-3">
                    <div className="font-medium text-white">{lead.name}</div>
                  </td>
                  <td className="p-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-1 text-sm text-gray-300">
                        <Phone size={12} /> {lead.phone}
                      </div>
                      {lead.email && (
                        <div className="flex items-center gap-1 text-sm text-gray-300">
                          <Mail size={12} /> {lead.email}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="p-3">
                    <select
                      value={lead.status}
                      onChange={(e) => updateLeadStatus(lead.id, e.target.value)}
                      className={`px-2 py-1 rounded-full text-xs border-0 ${getStatusColor(lead.status)}`}
                    >
                      <option value="nouveau">Nouveau</option>
                      <option value="qualifié">Qualifié</option>
                      <option value="occupé">Occupé</option>
                      <option value="boîte vocale">Boîte vocale</option>
                      <option value="à rappeler">À rappeler</option>
                      <option value="converti">Converti</option>
                      <option value="refusé">Refusé</option>
                      <option value="perdu">Perdu</option>
                    </select>
                  </td>
                  <td className="p-3 text-center">
                    <span className="text-white font-medium">{lead.call_attempts || 0}</span>
                  </td>
                  <td className="p-3">
                    {lead.last_call && (
                      <div>
                        <span className="text-xs text-gray-300">
                          {new Date(lead.last_call.created_at).toLocaleTimeString()}
                        </span>
                        <span className="text-xs text-gray-500 ml-2">
                          {lead.last_call.result}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="p-3 font-mono text-gray-300">
                    {formatDuration(lead.last_call_duration)}
                  </td>
                  <td className="p-3">
                    <button
                      onClick={() => addCall(lead.id)}
                      className="p-1 hover:bg-green-500/20 rounded text-gray-400 hover:text-green-400"
                      title="Appeler"
                    >
                      <Phone size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredLeads.length > 0 && (
          <div className="flex items-center justify-between p-4 border-t border-gray-800">
            <p className="text-sm text-gray-500">
              {filteredLeads.length} leads • Page {currentPage}/{totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 bg-gray-800 rounded-lg disabled:opacity-50"
              >
                <ChevronLeft size={18} className="text-gray-400" />
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 bg-gray-800 rounded-lg disabled:opacity-50"
              >
                <ChevronRight size={18} className="text-gray-400" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
